/**
 * DICE.app's pilot-aware rows — derives the rollable GRIT / HASE / skill-trigger rows
 * from a pilot's own data, the SAME three sources the PILOT sheet (`PilotBuildSheet.tsx`) already
 * renders on the credential (GRIT badge, HASE grid, skill triggers list) — so a bonus picked
 * in DICE.app always matches a number the pilot can already see on their license. Kept pure
 * and colocated with `DiceApp.tsx`, mirroring `checkNotation.ts`'s split (component vs. its
 * logic half).
 *
 * **A rank-0 skill never appears** (unowned, mirrors the license's own `rank > 0` filter), but
 * an owned skill/HASE stat *can* still yield a `bonus: 0` row — a HASE skill nobody has put a
 * point into is still a real, pickable +0.
 */
import { triggerBonus, type PilotData } from "@lancer-vtt/domain";
import type { ComputedPilot } from "@lancer-vtt/rules";
import type { Compendium } from "@lancer-vtt/compendium";

/** One pickable row: `label` is shown on the button and fed straight into the Bonus stepper's caption; `bonus` is the flat integer applied. */
export type BonusRow = { key: string; label: string; bonus: number };

/**
 * The GRIT roll caption. **The single definition** — the PILOT sheet's GRIT roll
 * button imports this rather than restating the string, so the two can't drift.
 */
export const GRIT_BONUS_LABEL = "GRIT";

/**
 * HASE stat → its roll caption (the full word, e.g. "HULL" — NOT the credential's own
 * abbreviated badge text "HUL", a separate, deliberately different string owned by
 * the old license's display chrome). **The single definition** both `pilotBonusRows`
 * below and the unit sheet's HASE row read from, so a rename can't leave the
 * two disagreeing about what a bonus is called.
 */
export const HASE_BONUS_LABEL: Record<"hull" | "agi" | "sys" | "eng", string> = {
  hull: "HULL",
  agi: "AGILITY",
  sys: "SYSTEMS",
  eng: "ENGINEERING",
};

/** One HASE axis's line on DICE's MECH tiles: what the skill covers, in a few words. */
const HASE_HINT: Record<keyof typeof HASE_BONUS_LABEL, string> = {
  hull: "Brute force and toughness.",
  agi: "Speed, balance, evasion.",
  sys: "Sensors, hacking, electronic warfare.",
  eng: "Reactor, heat, repairs.",
};

/** What a roll is, for DICE's ROLLING bar. */
export type RollKind = "PILOT TRIGGER" | "PILOT" | "MECH SKILL";

/** A row on DICE's roll sheet: a {@link BonusRow} with its one-line hint and kind. */
export type SheetRow = BonusRow & { hint: string; kind: RollKind };

/**
 * DICE's roll sheet for `pilot`, in its two sections: PILOT (owned skill triggers with their
 * compendium description, then GRIT) and MECH (the four HASE axes, all of them, at any value). A
 * rank-0 trigger is unowned and never appears.
 */
export function diceSheetRows(pilot: PilotData, computed: ComputedPilot | undefined, compendium: Compendium): { pilot: SheetRow[]; mech: SheetRow[] } {
  const triggers: SheetRow[] = [];
  for (const [id, rank] of Object.entries(pilot.skills)) {
    if (rank <= 0) continue;
    const item = compendium.getById("skill", id);
    triggers.push({ key: `trigger:${id}`, label: item?.name ?? id, bonus: triggerBonus(rank), hint: item?.description ?? "", kind: "PILOT TRIGGER" });
  }
  const grit: SheetRow = { key: "grit", label: GRIT_BONUS_LABEL, bonus: computed?.grit ?? Math.ceil(pilot.level / 2), hint: "No trigger fits: a flat pilot roll.", kind: "PILOT" };
  const mech = (["hull", "agi", "sys", "eng"] as const).map(
    (axis): SheetRow => ({ key: `hase:${axis}`, label: HASE_BONUS_LABEL[axis], bonus: pilot.mechSkills[axis], hint: HASE_HINT[axis], kind: "MECH SKILL" }),
  );
  return { pilot: [...triggers, grit], mech };
}

/**
 * The pilot-aware bonus rows for `pilot`: GRIT first, then HASE (all four, regardless of
 * value), then owned skill triggers (rank > 0 only) resolved to their compendium name. The same
 * rows as {@link diceSheetRows}, flat, for the sheets' roll-caption pin.
 */
export function pilotBonusRows(pilot: PilotData, computed: ComputedPilot | undefined, compendium: Compendium): BonusRow[] {
  const rows = diceSheetRows(pilot, computed, compendium);
  const grit = rows.pilot[rows.pilot.length - 1]!;
  return [grit, ...rows.mech, ...rows.pilot.slice(0, -1)].map(({ key, label, bonus }) => ({ key, label, bonus }));
}
